import { feedTime, fullDate, isFresh } from "../timestamps.ts";

interface LiveEntry {
  el: HTMLTimeElement;
  time: number;
}

// Two shared tickers: fresh stamps count down by the second, settled ones
// refresh every 30s. Entries migrate between the sets as they age (or as
// skewed future stamps approach), so cards need no per-post timers and no
// teardown beyond the route signal; detached entries are pruned on each tick.
const fresh = new Set<LiveEntry>();
const settled = new Set<LiveEntry>();
let fastTimer: ReturnType<typeof setInterval> | undefined;
let slowTimer: ReturnType<typeof setInterval> | undefined;
let visibilityHooked = false;

function render(entry: LiveEntry, now: Date): void {
  entry.el.textContent = feedTime(new Date(entry.time), now);
}

function tickFast(): void {
  const now = new Date();
  for (const entry of fresh) {
    if (!entry.el.isConnected) {
      fresh.delete(entry);
      continue;
    }
    if (!isFresh(new Date(entry.time), now)) {
      fresh.delete(entry);
      settled.add(entry);
      ensureSlow();
    }
    render(entry, now);
  }
  if (fresh.size === 0 && fastTimer !== undefined) {
    clearInterval(fastTimer);
    fastTimer = undefined;
  }
}

function tickSlow(): void {
  const now = new Date();
  for (const entry of settled) {
    if (!entry.el.isConnected) {
      settled.delete(entry);
      continue;
    }
    if (isFresh(new Date(entry.time), now)) {
      settled.delete(entry);
      fresh.add(entry);
      ensureFast();
    }
    render(entry, now);
  }
  if (settled.size === 0 && slowTimer !== undefined) {
    clearInterval(slowTimer);
    slowTimer = undefined;
  }
}

function ensureFast(): void {
  if (fastTimer === undefined) fastTimer = setInterval(tickFast, 1_000);
  hookVisibility();
}

function ensureSlow(): void {
  if (slowTimer === undefined) slowTimer = setInterval(tickSlow, 30_000);
  hookVisibility();
}

function hookVisibility(): void {
  if (!visibilityHooked && typeof document !== "undefined") {
    visibilityHooked = true;
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) {
        tickFast();
        tickSlow();
      }
    });
  }
}

/** Relative `<time>`, kept fresh by the shared tickers. Static when its signal is already aborted. */
export function RelativeTime(date: Date, signal?: AbortSignal): HTMLTimeElement {
  const now = new Date();
  const element = <time class="post-time" title={fullDate(date)}>{feedTime(date, now)}
  </time> as HTMLTimeElement;
  if (!Number.isNaN(date.getTime())) element.dateTime = date.toISOString();
  if (signal?.aborted) return element;
  const entry = { el: element, time: date.getTime() };
  if (isFresh(date, now)) {
    fresh.add(entry);
    ensureFast();
  } else {
    settled.add(entry);
    ensureSlow();
  }
  signal?.addEventListener("abort", () => {
    fresh.delete(entry);
    settled.delete(entry);
  }, { once: true });
  return element;
}

/** Focal `<time>`: absolute with seconds, never needs refreshing. */
export function AbsoluteTime(date: Date): HTMLTimeElement {
  const element = <time class="post-time">{fullDate(date)}</time> as HTMLTimeElement;
  if (!Number.isNaN(date.getTime())) element.dateTime = date.toISOString();
  return element;
}
